import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import authService, { User, LoginRequest, SignupRequest } from '../services/authService';
import trackStoreAction from '../utils/trackStoreAction';

// Define auth store state
interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  
  // Actions
  login: (credentials: LoginRequest) => Promise<void>;
  signup: (userData: SignupRequest) => Promise<void>;
  logout: () => void;
  fetchCurrentUser: (forceRefresh?: boolean) => Promise<void>;
  clearError: () => void;
  setSSOUser: (user: User) => void;
  updateUser: (userData: Partial<User>) => void;
  synchronizeUserData: () => Promise<void>; // New function to sync with server
}

// Flag to track if a fetchCurrentUser operation is in progress
let _fetchUserInProgress = false;

// Store name for tracking
const STORE_NAME = 'authStore';

// Create the auth store with persistence
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: false,
      isAuthenticated: false,
      error: null,

      // Login action
      login: async (credentials: LoginRequest) => {
        set({ isLoading: true, error: null });
        try {
          // Track login attempt (without sensitive data)
          trackStoreAction('login_attempt', STORE_NAME, { 
            email_domain: credentials.email.split('@')[1] 
          });
          
          const response = await authService.login(credentials);
          
          // Track successful login
          trackStoreAction('login_success', STORE_NAME, {
            user_id: response.user?._id
          });
          
          set({ 
            user: response.user,
            isAuthenticated: true,
            isLoading: false 
          });
        } catch (error: any) {
          // Track login failure (without sensitive data)
          trackStoreAction('login_error', STORE_NAME, { 
            error_message: error.message || 'Unknown error'
          });
          
          set({ 
            error: error.message || 'Failed to login',
            isLoading: false,
            isAuthenticated: false
          });
          throw error;
        }
      },

      // Signup action
      signup: async (userData: SignupRequest) => {
        set({ isLoading: true, error: null });
        try {
          // Track signup attempt (without sensitive data)
          trackStoreAction('signup_attempt', STORE_NAME, { 
            email_domain: userData.email.split('@')[1] 
          });
          
          const response = await authService.signup(userData);
          
          // Track successful signup
          trackStoreAction('signup_success', STORE_NAME, {
            user_id: response.user?._id
          });
          
          set({ 
            user: response.user,
            isAuthenticated: true,
            isLoading: false 
          });
        } catch (error: any) {
          // Track signup failure
          trackStoreAction('signup_error', STORE_NAME, { 
            error_message: error.message || 'Unknown error'
          });
          
          set({ 
            error: error.message || 'Failed to signup',
            isLoading: false,
            isAuthenticated: false
          });
          throw error;
        }
      },

      // Logout action
      logout: () => {
        const user = get().user;
        
        console.log("AUTH STORE: Logout action triggered", {
          hasUser: !!user,
          userId: user?._id,
          // Check if tokens exist before logout
          hasToken: !!localStorage.getItem('mocktrail-token'),
          hasRefreshToken: !!localStorage.getItem('mocktrail-refresh-token')
        });
        
        // Track logout with user information if available
        if (user) {
          trackStoreAction('logout', STORE_NAME, {
            user_id: user._id
          });
        } else {
          trackStoreAction('logout', STORE_NAME);
        }
        
        try {
          console.log("AUTH STORE: Calling authService.logout()");
          
          // Call authService.logout to handle server API call and cleanup
          authService.logout(true); // Pass true for serverLogout
          
          console.log("AUTH STORE: authService.logout completed");
        } catch (error) {
          console.error("AUTH STORE: Error during logout", error);
        }
        
        // Persist selected theme in local storage
        const savedTheme = localStorage.getItem('theme');
        // Clear local storage items
        localStorage.clear();

        // Restore saved theme
        if (savedTheme) {
          localStorage.setItem('theme', savedTheme);
        }

        // Update store state
        set({ 
          user: null,
          isAuthenticated: false,
          error: null
        });
        
        console.log("AUTH STORE: Logout completed, store state updated");
      },

      // Fetch current user
      fetchCurrentUser: async (forceRefresh = false) => {
        // Don't fetch if not authenticated
        if (!authService.isAuthenticated()) {
          set({ isAuthenticated: false, user: null });
          return;
        }

        // If a fetch is already in progress and we're not forcing a refresh, wait for it to complete
        if (_fetchUserInProgress && !forceRefresh) {
          console.log('AUTH STORE - Skip fetchCurrentUser: A fetch is already in progress');
          return;
        }

        // Get current user from the store
        const currentUser = get().user;

        // If we already have complete user data and we're not forcing a refresh, use cached data
        if (!forceRefresh && currentUser && currentUser._id && currentUser.email) {
          console.log('AUTH STORE - Skip fetchCurrentUser: Complete user data already exists in store:', currentUser._id);
          return;
        }

        // Check for cached user data in authService
        const cachedUser = authService.getCachedUserData();
        if (!forceRefresh && cachedUser && cachedUser._id && cachedUser.email) {
          console.log('AUTH STORE - Using cached user data from authService:', cachedUser._id);
          set({ 
            user: cachedUser,
            isAuthenticated: true,
            isLoading: false 
          });
          return;
        }

        _fetchUserInProgress = true;
        set({ isLoading: true });
        
        try {
          console.log('AUTH STORE - Fetching fresh user data from API');
          const user = await authService.getCurrentUser(forceRefresh);
          
          // Log detailed user information to debug profile picture issues
          console.log('AUTH STORE - User data fetched:', {
            ...user,
            profilePictureExists: !!user?.profilePicture,
            allKeys: Object.keys(user || {})
          });
          
          // Track successful user fetch
          trackStoreAction('fetch_user_success', STORE_NAME, {
            user_id: user?._id,
            subscription_plan: user?.subscriptionPlan
          });

          // Update localStorage to ensure external changes are persisted
          // This will update both the store state and the persisted storage
          set({ 
            user,
            isAuthenticated: true,
            isLoading: false 
          });

          // Also update cached user data in AuthService 
          // (this acts as a secondary cache)
          if (user && authService.setCachedUserData) {
            authService.setCachedUserData(user);
          }

        } catch (error: any) {
          // Track fetch error
          trackStoreAction('fetch_user_error', STORE_NAME, {
            error_message: error.message || 'Unknown error'
          });
          
          set({ 
            error: error.message || 'Failed to fetch user data',
            isLoading: false,
            isAuthenticated: false,
            user: null
          });
        } finally {
          _fetchUserInProgress = false;
        }
      },

      // Clear error message
      clearError: () => set({ error: null }),
      
      // Set user directly after SSO login
      setSSOUser: (user: User) => {
        // Log detailed user information from SSO login
        console.log('AUTH STORE - SSO user data:', {
          ...user,
          profilePictureExists: !!user?.profilePicture,
          allKeys: Object.keys(user || {})
        });
        
        // Track the SSO login
        trackStoreAction('sso_login_success', STORE_NAME, {
          user_id: user._id,
          email_domain: user.email.split('@')[1]
        });
        
        // Update the auth state
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null
        });
      },
      
      // Update user data partially
      updateUser: (userData: Partial<User>) => {
        const currentUser = get().user;
        if (!currentUser) return;
        
        // Track user update
        trackStoreAction('update_user', STORE_NAME, {
          user_id: currentUser._id,
        });
        
        // Merge current user with new data
        const updatedUser = {
          ...currentUser,
          ...userData
        };
        
        set({
          user: updatedUser
        });
      },

      // Synchronize user data with the server
      synchronizeUserData: async () => {
        console.log('AUTH STORE - Synchronizing user data with server');
        
        if (!authService.isAuthenticated()) {
          console.log('AUTH STORE - Not authenticated, skipping synchronization');
          return;
        }
        
        set({ isLoading: true });
        
        try {
          // Force a fresh API call to get latest user data
          const user = await authService.getCurrentUser(true);
          
          if (user) {
            console.log('AUTH STORE - Synchronized with fresh user data:', {
              userId: user._id,
              updatedFields: Object.keys(user)
            });
            
            // Update store with fresh data
            set({ 
              user, 
              isAuthenticated: true,
              isLoading: false,
              error: null
            });
            
            // Also update cached user data in authService if available
            if (authService.setCachedUserData) {
              authService.setCachedUserData(user);
            }
          } else {
            console.warn('AUTH STORE - Synchronization returned null user data');
          }
        } catch (error: any) {
          console.error('AUTH STORE - Error synchronizing user data:', error);
          set({ 
            error: error.message || 'Failed to synchronize user data',
            isLoading: false
          });
        } finally {
          set({ isLoading: false });
        }
      }
    }),
    {
      name: 'mocktrail-auth-storage',
      storage: createJSONStorage(() => localStorage),
      // Only persist these fields
      partialize: (state) => ({ 
        user: state.user,
        isAuthenticated: state.isAuthenticated
      }),
    }
  )
);

// Expose the auth store to the window object for direct access from non-React components
if (typeof window !== 'undefined') {
  // @ts-ignore
  window.useAuthStore = useAuthStore;
} 